import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { ArrowLeft, Save, Send, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export default function BlogEditor({ user }) {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const isEditing = !!blogId;
  const quillRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    meta_title: '',
    meta_description: '',
    target_keyword: '',
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ]
  }), []);

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [blogId]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/cms/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm(res.data);
      setSlugEdited(true);
    } catch (err) {
      alert('Blog not found');
      navigate('/dashboard/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm(prev => ({
      ...prev,
      title,
      slug: slugEdited ? prev.slug : slugify(title),
      meta_title: prev.meta_title || title
    }));
  };

  const handleSlugChange = (e) => {
    setSlugEdited(true);
    setForm(prev => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleSubmit = async (statusOverride) => {
    if (!form.title.trim() || !form.content.trim()) {
      alert('Title and content are required');
      return;
    }
    if (!form.slug.trim()) {
      alert('Slug is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...form, status: statusOverride || form.status };

      if (isEditing) {
        await axios.put(`${API}/cms/blogs/${blogId}`, payload, { headers });
      } else {
        await axios.post(`${API}/cms/blogs`, payload, { headers });
      }
      navigate('/dashboard/blogs');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading blog...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="blog-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/blogs')}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
            data-testid="back-to-blogs"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {isEditing ? 'Edit Blog' : 'Create Blog'}
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {isEditing ? 'Update your blog post' : 'Write a new blog post for your website'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {form.status === 'published' && isEditing && (
            <button
              onClick={() => window.open(`/blog/${form.slug}`, '_blank')}
              className="flex items-center gap-2 px-4 py-2.5 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium"
              data-testid="preview-blog-btn"
            >
              <Eye className="w-4 h-4" />
              View Live
            </button>
          )}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2.5 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium"
            data-testid="toggle-preview-btn"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={() => handleSubmit('draft')}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium text-sm transition-colors disabled:opacity-50"
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4" />
            Save Draft
          </button>
          <button
            onClick={() => handleSubmit('published')}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50"
            data-testid="publish-btn"
          >
            <Send className="w-4 h-4" />
            {isEditing ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {showPreview ? (
        /* Preview Mode */
        <div className="bg-white rounded-2xl border border-slate-200/60 p-8 shadow-sm" data-testid="blog-preview">
          {form.featured_image && (
            <img src={form.featured_image} alt="" className="w-full h-64 object-cover rounded-xl mb-6" />
          )}
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>{form.title || 'Untitled'}</h1>
          {form.excerpt && <p className="text-lg text-slate-500 mb-6">{form.excerpt}</p>}
          <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: form.content }} />
        </div>
      ) : (
        /* Edit Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={handleTitleChange}
                  placeholder="Enter blog title..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-lg"
                  data-testid="blog-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-sm">/blog/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={handleSlugChange}
                    placeholder="url-slug"
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm font-mono"
                    data-testid="blog-slug-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief summary of the blog post..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm resize-none"
                  data-testid="blog-excerpt-input"
                />
              </div>
            </div>

            {/* Rich Text Editor */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-3">Content</label>
              <div className="blog-editor-wrapper" data-testid="blog-content-editor">
                <ReactQuill
                  ref={quillRef}
                  value={form.content}
                  onChange={(val) => setForm(prev => ({ ...prev, content: val }))}
                  modules={modules}
                  theme="snow"
                  placeholder="Write your blog content here..."
                  style={{ minHeight: '400px' }}
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm"
                data-testid="blog-status-select"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-2">Featured Image URL</label>
              <input
                type="text"
                value={form.featured_image}
                onChange={(e) => setForm(prev => ({ ...prev, featured_image: e.target.value }))}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm"
                data-testid="blog-image-input"
              />
              {form.featured_image && (
                <img src={form.featured_image} alt="Preview" className="mt-3 w-full h-32 object-cover rounded-lg border border-slate-200" />
              )}
            </div>

            {/* SEO */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">SEO Settings</h3>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Meta Title</label>
                <input
                  type="text"
                  value={form.meta_title}
                  onChange={(e) => setForm(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="SEO page title"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm"
                  data-testid="blog-meta-title"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Meta Description</label>
                <textarea
                  value={form.meta_description}
                  onChange={(e) => setForm(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="SEO meta description"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm resize-none"
                  data-testid="blog-meta-desc"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Target Keyword</label>
                <input
                  type="text"
                  value={form.target_keyword}
                  onChange={(e) => setForm(prev => ({ ...prev, target_keyword: e.target.value }))}
                  placeholder="Primary keyword"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300 text-sm"
                  data-testid="blog-keyword"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quill editor styles */}
      <style>{`
        .blog-editor-wrapper .ql-container {
          min-height: 400px;
          font-size: 15px;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
        }
        .blog-editor-wrapper .ql-toolbar {
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          background: #f8fafc;
        }
        .blog-editor-wrapper .ql-editor {
          min-height: 380px;
        }
      `}</style>
    </div>
  );
}
